import { Editor, Transforms, Path, Text } from 'slate';

import { LOGICAL_OPERATOR_SUGGESTIONS } from '../constants';
import { CustomText, LogicalOperatorElement } from '../types';

export const operatorHandlers = {
  // [PURPOSE]: Handles the conversion of a text node marked with 'convertToLogicalOperator' into a 'logical-operator' element (e.g., AND, OR).
  convertToLogicalOperator(editor: Editor, node: Node, path: Path) {
    if (!(Text.isText(node) && (node as CustomText).convertToLogicalOperator)) {
      return false;
    }

    if (!Editor.hasPath(editor, path)) return false;

    // Get operator text from node property, or fall back to trimmed text
    const operatorText = (node as any).operatorText || node.text.trim();

    // Ensure it's a valid logical operator
    const upperOperator = operatorText.toUpperCase();
    if (!LOGICAL_OPERATOR_SUGGESTIONS.includes(upperOperator)) {
      Transforms.unsetNodes(editor, ['convertToLogicalOperator', 'operatorText'], { at: path });

      return false;
    }

    Editor.withoutNormalizing(editor, () => {
      // Create and insert the logical operator element
      const logicalOpNode = {
        children: [{ text: '' }],
        operator: upperOperator,
        type: 'logical-operator',
      };

      // Replace the node and pre/post spaces
      Transforms.removeNodes(editor, { at: path });
      Transforms.insertNodes(
        editor,
        [
          { text: ' ' } as CustomText,
          logicalOpNode as LogicalOperatorElement,
          { text: ' ' } as CustomText,
        ],
        { at: path },
      );

      // Select after the node
      const nodePath = Path.next(path);
      const spacePath = Path.next(nodePath);
      if (Editor.hasPath(editor, spacePath)) {
        Transforms.select(editor, Editor.end(editor, spacePath));
      } else {
        Transforms.select(editor, Editor.end(editor, path));
      }
    });

    return true;
  },

  // [PURPOSE]: Detects if a text node represents a logical operator (AND/OR, case-insensitive with trailing space, or exact case without space) and marks it for conversion ('convertToLogicalOperator: true').
  detectLogicalOperatorText(editor: Editor, node: Node, path: Path) {
    if (!(Text.isText(node) && !node.convertToLogicalOperator && !node.isEditing)) {
      return false;
    }

    // Check for case insensitive logical operators (trimmed) with trailing space in original
    // OR exact capitalized operators (trimmed) without trailing space
    const { text } = node;
    if (!text) return false;

    const hasTrailingSpace = text.endsWith(' '); // More reliable trailing space check

    const trimmedText = text.trim();

    let operator: string | null = null;

    // Check 1: Exact capitalized match on trimmed text (convert regardless of spaces)
    if (trimmedText === 'AND' || trimmedText === 'OR') {
      operator = trimmedText;
    }
    // Check 2: Case-insensitive match that REQUIRES trailing space
    else {
      if (
        hasTrailingSpace &&
        (trimmedText.toLowerCase() === 'and' || trimmedText.toLowerCase() === 'or')
      ) {
        operator = trimmedText.toUpperCase();
      }
    }

    if (operator) {
      // Set property for conversion using any to bypass type checking
      Transforms.setNodes(
        editor,
        {
          convertToLogicalOperator: true,
          operatorText: operator, // Use the determined operator
        } as any,
        { at: path },
      );

      return true;
    }

    return false;
  },
};

export default operatorHandlers;
